/**
 * Shared admin design layer - the pieces every admin page is built from, so the
 * whole console reads as one system. The signature element is <AdminHero>: a
 * dark espresso band with a gold eyebrow, serif title and optional live stat
 * chips. Architecture is unchanged - pages keep their own data and actions and
 * just compose these primitives.
 */
import type { ReactNode } from 'react';

export interface HeroStat { label: string; value: ReactNode; accent?: 'gold' | 'ok' | 'bad' | 'sage'; }

const ACCENT: Record<string, string> = { gold: 'text-gold-light', ok: 'text-ok', bad: 'text-bad', sage: 'text-sage-light', };

/** The dark signature header used on every admin page. */
export function AdminHero({ eyebrow, title, subtitle, right, stats }: {
  eyebrow: string; title: ReactNode; subtitle?: ReactNode; right?: ReactNode; stats?: HeroStat[];
}) {
  return (
    <div className="section-dark relative overflow-hidden bg-ink text-cream rounded-bold p-7 md:p-8 shadow-luxe">
      <div className="absolute inset-0 bg-gold-mesh opacity-40" />
      <div className="absolute inset-0 bg-dot-light bg-[length:24px_24px] opacity-25" />
      <div className="relative flex items-start justify-between gap-6 flex-wrap">
        <div className="min-w-0">
          <div className="mono text-[10px] tracking-[0.34em] uppercase text-gold-light/80">{eyebrow}</div>
          <h1 className="display text-4xl md:text-5xl mt-2.5 leading-none">{title}</h1>
          {subtitle && <p className="text-cream/60 text-[13.5px] mt-2.5 max-w-[64ch] leading-relaxed">{subtitle}</p>}
        </div>
        {right && <div className="shrink-0">{right}</div>}
      </div>
      {stats && stats.length > 0 && (
        <div className="relative mt-6 flex flex-wrap gap-x-10 gap-y-3 pt-5 border-t border-cream/10">
          {stats.map((s) => (
            <div key={s.label}>
              <div className="mono text-[9px] tracking-[0.22em] uppercase text-cream/45">{s.label}</div>
              <div className={`display text-2xl mt-1 leading-none ${s.accent ? ACCENT[s.accent] : 'text-cream'}`}>{s.value}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Search field with a leading glyph, sized for admin toolbars. */
export function SearchBox({ value, onChange, placeholder, className = 'w-72' }: {
  value: string; onChange: (v: string) => void; placeholder?: string; className?: string;
}) {
  return (
    <div className={`relative ${className}`}>
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-mute text-sm pointer-events-none">⌕</span>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="input w-full text-sm pl-9" />
    </div>
  );
}

/** Pill segmented control (string options). */
export function Segmented<T extends string>({ value, options, onChange }: {
  value: T; options: readonly { value: T; label: string }[]; onChange: (v: T) => void;
}) {
  return (
    <div className="inline-flex bg-cream-deep rounded-full p-1">
      {options.map((o) => (
        <button key={o.value} onClick={() => onChange(o.value)}
          className={`px-3.5 py-1.5 text-[12px] font-medium rounded-full transition ${value === o.value ? 'bg-ink text-cream' : 'text-mute hover:text-ink'}`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Initials avatar; tone picks a palette-tinted background. */
export function Avatar({ name, tone = 'sage', className = 'w-9 h-9 text-[12px]' }: {
  name: string; tone?: 'sage' | 'gold' | 'ink' | 'plum' | 'clay'; className?: string;
}) {
  const p = name.trim().split(/\s+/).filter(Boolean);
  const initials = ((p[0]?.[0] ?? '') + (p[1]?.[0] ?? '')).toUpperCase() || '·';
  const bg = tone === 'gold' ? 'bg-gold text-ink' : tone === 'ink' ? 'bg-ink text-cream'
    : tone === 'plum' ? 'bg-plum text-cream' : tone === 'clay' ? 'bg-clay text-cream' : 'bg-sage text-cream';
  return <span className={`shrink-0 rounded-full grid place-items-center font-semibold display ${bg} ${className}`}>{initials}</span>;
}

/** Status chip: a coloured dot + label. Pass Souqli pill classes for the fill. */
export function StatusPill({ label, pill, dot, className = 'text-[10px]' }: { label: string; pill: string; dot: string; className?: string; }) {
  return (
    <span className={`pill ${pill} ${className}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />{label}
    </span>
  );
}

/** Section heading with an optional right-aligned action. */
export function SectionHead({ title, right, count }: { title: string; right?: ReactNode; count?: number }) {
  return (
    <div className="flex items-end justify-between mt-9 mb-3.5">
      <div className="flex items-baseline gap-2.5">
        <h2 className="display text-2xl">{title}</h2>
        {count !== undefined && count > 0 && <span className="pill pill-bad text-[10px]">{count}</span>}
      </div>
      {right}
    </div>
  );
}

/** Small right-aligned count label for toolbars. */
export function Count({ n, noun }: { n: number; noun: string }) {
  return <span className="ml-auto pill bg-white border border-warmline text-mute text-[11px]">{n.toLocaleString()} {n === 1 ? noun : `${noun}s`}</span>;
}
